import React, { useState } from 'react';
import { DocumentTextIcon, ArrowDownTrayIcon, BuildingOffice2Icon, CalendarIcon, CreditCardIcon, TruckIcon, DocumentDuplicateIcon, CheckIcon } from '@heroicons/react/24/outline';
import type { XmlTerceiro } from '../../types/xmlTerceiros';
import { useBaixarDanfe, useBaixarXml } from '../../hooks/useXmlTerceiros';
import { money, formatCnpj, formatDateTime, statusNfeErp, type StatusNfeErpTone } from '../../utils/format';
import ParcelasDialog from './ParcelasDialog';
import CtesDaNfeDialog from './CtesDaNfeDialog';

interface XmlTerceirosListProps {
  xmlTerceiros: XmlTerceiro[];
  onSelect: (xml: XmlTerceiro) => void;
  isLoading: boolean;
}

const TONE_BADGE: Record<StatusNfeErpTone, string> = {
  ok: 'bg-ok-soft text-ok',
  warning: 'bg-warning-soft text-warning',
  danger: 'bg-danger-soft text-danger',
  neutral: 'bg-surface-2 text-tx-3',
};

/** Status REAL do ERP (TESTNFE.STATUS) + indicador de trânsito — único uso de cor semântica na linha. */
const StatusBadge: React.FC<{ erpStatus?: string | null; emTransito?: boolean }> = ({ erpStatus, emTransito }) => {
  const { label, tone } = statusNfeErp(erpStatus);
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${TONE_BADGE[tone]}`}>{label}</span>
      {emTransito && (
        <span
          className="inline-flex items-center gap-1 rounded-full bg-accent-soft px-2 py-0.5 text-[11px] font-medium text-accent-text"
          title="Mercadoria ainda em trânsito (sem entrada no estoque)"
        >
          <TruckIcon className="h-3 w-3" /> Trânsito
        </span>
      )}
    </span>
  );
};

const XmlTerceirosList: React.FC<XmlTerceirosListProps> = ({ xmlTerceiros, onSelect, isLoading }) => {
  const baixarDanfeMutation = useBaixarDanfe();
  const baixarXmlMutation = useBaixarXml();
  const [parcelasNfe, setParcelasNfe] = useState<XmlTerceiro | null>(null);
  const [ctesNfe, setCtesNfe] = useState<XmlTerceiro | null>(null);
  const [copiadoId, setCopiadoId] = useState<number | null>(null);

  const handleOpenParcelas = (e: React.MouseEvent, xml: XmlTerceiro) => { e.stopPropagation(); setParcelasNfe(xml); };
  const handleOpenCtes = (e: React.MouseEvent, xml: XmlTerceiro) => { e.stopPropagation(); setCtesNfe(xml); };
  const handleBaixarDanfe = (e: React.MouseEvent, gid: number) => { e.stopPropagation(); baixarDanfeMutation.mutate(gid); };
  const handleBaixarXml = (e: React.MouseEvent, gid: number) => { e.stopPropagation(); baixarXmlMutation.mutate(gid); };
  const handleCopiarChave = async (e: React.MouseEvent, xml: XmlTerceiro) => {
    e.stopPropagation();
    if (!xml.id) return;
    try {
      await navigator.clipboard.writeText(xml.id);
      setCopiadoId(xml.gid);
      setTimeout(() => setCopiadoId((cur) => (cur === xml.gid ? null : cur)), 1500);
    } catch { /* silencioso */ }
  };

  // Skeleton estrutural (nunca tela em branco).
  if (isLoading) {
    return (
      <div className="overflow-hidden rounded-iris-lg border border-line bg-surface">
        <div className="divide-y divide-line">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3 px-4 py-3">
              <div className="h-3 w-10 animate-pulse rounded-iris-sm bg-surface-2" />
              <div className="h-3 flex-1 animate-pulse rounded-iris-sm bg-surface-2" />
              <div className="hidden h-3 w-24 animate-pulse rounded-iris-sm bg-surface-2 md:block" />
              <div className="hidden h-3 w-20 animate-pulse rounded-iris-sm bg-surface-2 md:block" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!xmlTerceiros || xmlTerceiros.length === 0) {
    return (
      <div className="rounded-iris-lg border border-line bg-surface px-6 py-12 text-center">
        <DocumentTextIcon className="mx-auto h-10 w-10 text-tx-3" />
        <h3 className="mt-3 text-sm font-semibold text-tx">Nenhuma NF-e encontrada</h3>
        <p className="mt-1 text-sm text-tx-3">Nenhuma nota fiscal de terceiros no período/filtros aplicados.</p>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-iris-lg border border-line bg-surface">
      {/* Header da tabela densa — só de md pra cima (fronteira tabela↔card). */}
      <div className="hidden border-b border-line bg-surface-2 px-4 py-2 md:block">
        <div className="grid grid-cols-12 gap-2 text-[11px] font-medium uppercase tracking-wide text-tx-3">
          <div className="col-span-1">NF-e</div>
          <div className="col-span-3">Emitente</div>
          <div className="col-span-2">Emissão</div>
          <div className="col-span-2 text-right">Valor</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-2 text-right">Ações</div>
        </div>
      </div>

      <div className="divide-y divide-line">
        {xmlTerceiros.map((xml) => (
          <div
            key={xml.gid}
            onClick={() => onSelect(xml)}
            className="cursor-pointer transition-colors hover:bg-surface-2"
          >
            {/* ── Mobile / tablet estreito (< md): card ── */}
            <div className="p-3 md:hidden">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-tx">NF-e {xml.numero}</span>
                    <StatusBadge erpStatus={xml.erpStatus} emTransito={xml.emTransito} />
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-semibold tabular-nums text-tx">{money(xml.valorTotalNf)}</div>
                  <div className="text-[11px] tabular-nums text-tx-3">{formatDateTime(xml.dataHoraEmissao)}</div>
                </div>
              </div>

              <dl className="mt-2.5 space-y-1.5 text-[13px]">
                <div className="flex items-center gap-2 text-tx">
                  <BuildingOffice2Icon className="h-4 w-4 flex-shrink-0 text-tx-3" />
                  <span className="truncate font-medium">{xml.emitenteNome || xml.emitenteFantasia || 'Emitente não informado'}</span>
                </div>
                <div className="flex items-center gap-2 text-tx-3">
                  <DocumentTextIcon className="h-4 w-4 flex-shrink-0" />
                  <span className="tabular-nums">{formatCnpj(xml.emitenteCnpj)}</span>
                </div>
              </dl>

              {/* Ações — 2 linhas no mobile pra não espremer os rótulos; neutras por token; alvo ≥ 44px.
                  Linha 1: chave + drill (CT-e / Parcelas). Linha 2: downloads (PDF / XML). */}
              <div className="mt-3 space-y-2 border-t border-line pt-3">
                <div className="flex gap-2">
                  {xml.id && (
                    <button
                      onClick={(e) => handleCopiarChave(e, xml)}
                      className="inline-flex min-h-[44px] w-11 flex-shrink-0 items-center justify-center rounded-iris border border-line bg-surface-2 text-tx-2 transition-colors hover:bg-surface"
                      title="Copiar chave de acesso"
                      aria-label="Copiar chave de acesso"
                    >
                      {copiadoId === xml.gid ? <CheckIcon className="h-4 w-4 text-ok" /> : <DocumentDuplicateIcon className="h-4 w-4" />}
                    </button>
                  )}
                  <button
                    onClick={(e) => handleOpenCtes(e, xml)}
                    className="inline-flex min-h-[44px] flex-1 items-center justify-center gap-1.5 rounded-iris border border-line bg-surface-2 text-sm font-medium text-tx transition-colors hover:bg-surface"
                    title="CT-e desta NF-e"
                  >
                    <TruckIcon className="h-4 w-4 text-tx-2" /> CT-e
                  </button>
                  <button
                    onClick={(e) => handleOpenParcelas(e, xml)}
                    className="inline-flex min-h-[44px] flex-1 items-center justify-center gap-1.5 rounded-iris border border-line bg-surface-2 text-sm font-medium text-tx transition-colors hover:bg-surface"
                    title="Ver parcelamento"
                  >
                    <CreditCardIcon className="h-4 w-4 text-tx-2" /> Parcelas
                  </button>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={(e) => handleBaixarDanfe(e, xml.gid)}
                    disabled={baixarDanfeMutation.isPending}
                    className="inline-flex min-h-[44px] flex-1 items-center justify-center gap-1.5 rounded-iris border border-line bg-surface-2 text-sm font-medium text-tx transition-colors hover:bg-surface disabled:opacity-50"
                  >
                    <ArrowDownTrayIcon className="h-4 w-4 text-tx-2" /> PDF
                  </button>
                  <button
                    onClick={(e) => handleBaixarXml(e, xml.gid)}
                    disabled={baixarXmlMutation.isPending}
                    className="inline-flex min-h-[44px] flex-1 items-center justify-center gap-1.5 rounded-iris border border-line bg-surface-2 text-sm font-medium text-tx transition-colors hover:bg-surface disabled:opacity-50"
                  >
                    <DocumentTextIcon className="h-4 w-4 text-tx-2" /> XML
                  </button>
                </div>
              </div>
            </div>

            {/* ── Desktop (md+): linha densa ── */}
            <div className="hidden grid-cols-12 items-center gap-2 px-4 py-2 text-[13px] md:grid">
              <div className="col-span-1 min-w-0">
                <div className="font-semibold text-tx">{xml.numero}</div>
              </div>

              <div className="col-span-3 min-w-0">
                <div className="truncate font-medium text-tx" title={xml.emitenteNome ?? undefined}>{xml.emitenteNome || xml.emitenteFantasia || 'Não informado'}</div>
                <div className="truncate text-[11px] tabular-nums text-tx-3">{formatCnpj(xml.emitenteCnpj)}</div>
              </div>

              <div className="col-span-2 min-w-0">
                <div className="flex items-center gap-1 text-tx-2 tabular-nums">
                  <CalendarIcon className="h-3 w-3 flex-shrink-0 text-tx-3" />{formatDateTime(xml.dataHoraEmissao)}
                </div>
              </div>

              <div className="col-span-2 min-w-0 text-right">
                <div className="font-medium tabular-nums text-tx">{money(xml.valorTotalNf)}</div>
              </div>

              <div className="col-span-2 min-w-0">
                <StatusBadge erpStatus={xml.erpStatus} emTransito={xml.emTransito} />
              </div>

              <div className="col-span-2 flex items-center justify-end gap-0.5">
                {xml.id && (
                  <button
                    onClick={(e) => handleCopiarChave(e, xml)}
                    className="inline-flex h-7 w-7 items-center justify-center rounded-iris-sm text-tx-3 transition-colors hover:bg-surface-2 hover:text-tx"
                    title="Copiar chave de acesso"
                  >
                    {copiadoId === xml.gid ? <CheckIcon className="h-4 w-4 text-ok" /> : <DocumentDuplicateIcon className="h-4 w-4" />}
                  </button>
                )}
                <button
                  onClick={(e) => handleOpenCtes(e, xml)}
                  className="inline-flex h-7 w-7 items-center justify-center rounded-iris-sm text-tx-3 transition-colors hover:bg-surface-2 hover:text-tx"
                  title="CT-e desta NF-e"
                >
                  <TruckIcon className="h-4 w-4" />
                </button>
                <button
                  onClick={(e) => handleOpenParcelas(e, xml)}
                  className="inline-flex h-7 w-7 items-center justify-center rounded-iris-sm text-tx-3 transition-colors hover:bg-surface-2 hover:text-tx"
                  title="Ver parcelamento"
                >
                  <CreditCardIcon className="h-4 w-4" />
                </button>
                <button
                  onClick={(e) => handleBaixarDanfe(e, xml.gid)}
                  disabled={baixarDanfeMutation.isPending}
                  className="inline-flex h-7 w-7 items-center justify-center rounded-iris-sm text-tx-3 transition-colors hover:bg-surface-2 hover:text-tx disabled:opacity-50"
                  title="Baixar DANFE (PDF)"
                >
                  <ArrowDownTrayIcon className="h-4 w-4" />
                </button>
                <button
                  onClick={(e) => handleBaixarXml(e, xml.gid)}
                  disabled={baixarXmlMutation.isPending}
                  className="inline-flex h-7 w-7 items-center justify-center rounded-iris-sm text-tx-3 transition-colors hover:bg-surface-2 hover:text-tx disabled:opacity-50"
                  title="Baixar XML"
                >
                  <DocumentTextIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <ParcelasDialog
        nfeId={parcelasNfe?.gid ?? null}
        numeroNfe={parcelasNfe?.numero}
        isOpen={!!parcelasNfe}
        onClose={() => setParcelasNfe(null)}
      />

      <CtesDaNfeDialog
        nfeId={ctesNfe?.gid ?? null}
        numeroNfe={ctesNfe?.numero}
        isOpen={!!ctesNfe}
        onClose={() => setCtesNfe(null)}
      />
    </div>
  );
};

export default XmlTerceirosList;
